import { gql, gqlFetch, invalidateGqlCache } from '@/service/gql'
import { type SanitizedResult } from '@amzn/amp-gql'
import { imageFieldsFrag } from '@/service/fragments/imageFieldsFrag'
import { createSingleValueOverlay } from '@/util/localOverlay'
import {
    createLogPrefix,
    createPersistentItem,
    globalRef,
    logDebug,
    logWarn,
    nativeFetchRef,
    type Maybe,
    type Unsubscribe,
} from '@amazon/vinyl-util'
import { data, type ObservableValue } from '@amazon/vinyl-observable'
import { getAuth, signedIn } from '@/auth/authRef'
import { createErrorHandler } from '@/errorHandler'

const target = { logPrefix: createLogPrefix('userInfoService') }
const errorHandler = createErrorHandler(target)

// Drop server-side `api-cache` for userInfo reads that follow writes —
// without this, Firefly returns the pre-write payload until its cache
// expires, and the avatar/name updates appear to "not save".
const NO_API_CACHE_FLAGS = 'use-ups'

// Local write-through overlays. Even with cache invalidation, the Firefly
// gateway can take several seconds to surface a write. The overlay is read
// after every fetch and clobbers the server-returned name/imageUrl until
// the entry expires.
//
// Both overlays persist to localStorage so a page refresh during the
// gateway's catch-up window still shows the user's chosen value. The
// imageUrl overlay specifically stores a data URL (not a blob URL) —
// blob URLs are tied to the document and don't survive refresh.
const nameOverlay = createSingleValueOverlay<string>({
    persistence: createPersistentItem({
        key: 'overlay:userInfo:name',
        defaultValue: {},
    }),
    validateValue: (v): v is string => typeof v === 'string',
})
// `null` here means "explicitly cleared" so we can distinguish it from
// "not in overlay" (`undefined`) when we apply the override.
const imageUrlOverlay = createSingleValueOverlay<string | null>({
    persistence: createPersistentItem({
        key: 'overlay:userInfo:imageUrl',
        defaultValue: {},
    }),
    validateValue: (v): v is string | null => v === null || typeof v === 'string',
})
// Same write-through treatment as name/imageUrl — without it, a visibility
// toggle appears to silently revert if something re-reads userInfo (the
// cached, non-Fresh path) before Firefly's write propagates.
const visibilityOverlay = createSingleValueOverlay<'PUBLIC' | 'PRIVATE'>({
    persistence: createPersistentItem({
        key: 'overlay:userInfo:visibility',
        defaultValue: {},
    }),
    validateValue: (v): v is 'PUBLIC' | 'PRIVATE' => v === 'PUBLIC' || v === 'PRIVATE',
})

function applyOverlays(result: UserInfoResult): UserInfoResult {
    const overrideName = nameOverlay.read()
    const overrideImageUrl = imageUrlOverlay.read()
    const overrideVisibility = visibilityOverlay.read()
    if (
        overrideName === undefined &&
        overrideImageUrl === undefined &&
        overrideVisibility === undefined
    )
        return result
    const user = result.user
    if (!user) return result
    const next: UserInfoResult = {
        ...result,
        user: {
            ...user,
            ...(overrideName !== undefined ? { name: overrideName } : null),
            ...(overrideVisibility !== undefined ? { visibility: overrideVisibility } : null),
            ...(overrideImageUrl !== undefined
                ? {
                      // Replace the user's images list with a single entry
                      // for the new URL (or clear it on null) so
                      // pickProfileImageUrl picks up the override on its
                      // next call.
                      images:
                          overrideImageUrl === null
                              ? []
                              : [
                                    {
                                        url: overrideImageUrl,
                                        width: null,
                                        height: null,
                                        imageType: 'PROFILE',
                                        colorPalette: null,
                                    },
                                ],
                  }
                : null),
        },
    }
    return next
}

const userInfoQuery = gql(
    `query userInfo {
        user {
            id
            name
            tier
            playableAudioQualities
            visibility
            images {
                ...imageFields
            }
        }
        defaultProfileImage
    }`,
    [imageFieldsFrag]
)

export type UserInfoResult = SanitizedResult<typeof userInfoQuery>

/**
 * Session-scoped observable holding the current user-info payload. Any
 * surface that needs the user's name or avatar should subscribe to
 * this rather than calling fetchUserInfo() on its own — that way edits
 * propagate to every listener immediately.
 *
 * The first read calls {@link refreshUserInfo} lazily; later edits via
 * {@link updateUserProfile} synthesize a local update so subscribers
 * see the change without waiting on the gateway.
 */
const userInfoStateRef = globalRef(() => ({
    userInfo$: data<UserInfoResult | null>(null),
    loaded: false,
    stopAuth: null as Unsubscribe | null,
    // A registry reset (an app reload) disposes the state; without this its auth
    // listener would outlive it and refetch on every later sign-in.
    dispose() {
        this.stopAuth?.()
    },
}))

export function getUserInfo(): ObservableValue<UserInfoResult | null> {
    const state = userInfoStateRef.value
    // Only fetch when signed in: `userInfoQuery` is a per-user query the
    // gateway denies for anonymous callers (Access denied), so a lazy load
    // while signed out just errors. Leave `userInfo$` null until sign-in —
    // the auth subscription below fetches on the SIGNED_IN transition, and
    // every consumer already handles a null userInfo. `loaded` is not
    // latched here so a later signed-in read triggers the lazy fetch.
    if (!state.loaded && getAuth().isSignedIn()) {
        state.loaded = true
        // Fire-and-forget — subscribers get the value when it lands.
        refreshUserInfo().catch(errorHandler)
    }
    if (!state.stopAuth) {
        // Sign-in / sign-out changes the user behind the gateway. Drop
        // the stale value and re-fetch so the header avatar, profile
        // page, etc. all reflect the new identity. Without this the
        // first sign-in after a fresh load showed the old (anonymous)
        // userInfo because the lazy-load above had already cached the
        // previous result.
        state.stopAuth = signedIn().onData((isSignedIn, previous) => {
            if (previous == null) return
            if (isSignedIn) {
                refreshUserInfo().catch(errorHandler)
            } else {
                state.userInfo$.value = null
            }
        })
    }
    return state.userInfo$
}

/**
 * Re-reads userInfo from the gateway and pushes the result into
 * {@link getUserInfo}. Use after a write to make sure every subscriber
 * (header avatar, profile page, anywhere else) sees the new value.
 */
export async function refreshUserInfo(): Promise<UserInfoResult> {
    const result = await fetchUserInfo()
    userInfoStateRef.value.userInfo$.value = result
    return result
}

/**
 * Synchronous local update of {@link getUserInfo}'s value. Apply the same patch
 * the gateway will return when it propagates the write — the overlay
 * already does this for `applyOverlays`, but updating the observable
 * directly means subscribers see the change instantly without waiting
 * on the next fetch.
 */
function patchUserInfoLocally(patch: {
    name?: string
    imageUrl?: string
    visibility?: 'PUBLIC' | 'PRIVATE'
}): void {
    const current = userInfoStateRef.value.userInfo$.value
    if (!current?.user) return
    const next: UserInfoResult = {
        ...current,
        user: {
            ...current.user,
            ...(patch.name !== undefined ? { name: patch.name } : null),
            ...(patch.visibility !== undefined ? { visibility: patch.visibility } : null),
            ...(patch.imageUrl !== undefined
                ? {
                      // Replace the user's images list with a single entry
                      // for the new URL so pickProfileImageUrl picks it up.
                      images: [
                          {
                              url: patch.imageUrl,
                              width: null,
                              height: null,
                              imageType: 'PROFILE',
                              colorPalette: null,
                          },
                      ],
                  }
                : null),
        },
    }
    userInfoStateRef.value.userInfo$.value = next
}

async function awaitOverlayHydration(): Promise<void> {
    // Wait for every overlay's persistent item to load before merging.
    // Without this, the very first read after a cold start can race the
    // hydration and miss recently-persisted entries.
    await Promise.all([
        nameOverlay.whenHydrated(),
        imageUrlOverlay.whenHydrated(),
        visibilityOverlay.whenHydrated(),
    ])
}

export async function fetchUserInfo(): Promise<UserInfoResult> {
    // Wait for persisted overlay entries to hydrate before deciding
    // whether to use the cached path. If an overlay is in flight, a
    // recent write hasn't fully propagated through Firefly yet, and
    // the cached read would otherwise capture the gateway's pre-write
    // payload and pin it for an hour.
    await awaitOverlayHydration()
    if (
        nameOverlay.read() !== undefined ||
        imageUrlOverlay.read() !== undefined ||
        visibilityOverlay.read() !== undefined
    ) {
        // Overlay active → poll the gateway uncached so the cache fills
        // with a post-propagation response.
        return fetchUserInfoFresh()
    }
    const result = await gqlFetch(userInfoQuery, {}, { useCache: true })
    const merged = applyOverlays(result)
    userInfoStateRef.value.userInfo$.value = merged
    return merged
}

/**
 * Fetches user info bypassing the cache. Use after a profile edit so the
 * header avatar reflects the latest server state instead of stale cached data.
 *
 * Also invalidates the cached `userInfo` entry and opts out of the gateway's
 * `api-cache` so the response truly reflects the server, not whichever layer
 * was caching it.
 */
export async function fetchUserInfoFresh(): Promise<UserInfoResult> {
    await invalidateGqlCache(userInfoQuery, {})
    const result = await gqlFetch(userInfoQuery, {}, { featureFlags: NO_API_CACHE_FLAGS })
    await awaitOverlayHydration()
    const merged = applyOverlays(result)
    userInfoStateRef.value.userInfo$.value = merged
    return merged
}

/**
 * Picks the user's profile image URL, preferring `imageType: PROFILE` and
 * falling back to the smallest available image, then to the server-provided
 * default profile image.
 */
export function pickProfileImageUrl(result: UserInfoResult): string | null {
    const images = result.user?.images ?? []
    const profileOnly = images.filter((i) => i.imageType === 'PROFILE')
    const candidates = profileOnly.length ? profileOnly : images
    if (candidates.length) {
        const smallest = [...candidates].sort(
            (a, b) => (a.width ?? Infinity) - (b.width ?? Infinity)
        )[0]
        if (smallest.url) return smallest.url
    }
    return result.defaultProfileImage ?? null
}

// Schema (from `@amzn/music-firefly` dep):
//   profileImageUploadForm(id: String): MediaUploadForm
//   mediaUploadUrl(contentType: String): MediaUploadForm
//   publishUploadedMedia(id: String, mediaNamespace: String, url: String): MediaLocation
//
// We try profileImageUploadForm first (the named profile path) with the
// user's id; fall back to mediaUploadUrl(contentType) when the profile
// path returns an incomplete form. After the S3 POST succeeds we publish
// with the user id + a "USER_PROFILE_IMAGE" namespace.
const profileImageUploadFormQuery = gql(`
    query profileImageUploadForm($id: String) {
        profileImageUploadForm(id: $id) {
            fileFieldName
            formFields {
                key
                value
            }
            mediaId
            publishUrl
            url
        }
    }
`)

const mediaUploadUrlQuery = gql(`
    query mediaUploadUrl($contentType: String) {
        mediaUploadUrl(contentType: $contentType) {
            fileFieldName
            formFields {
                key
                value
            }
            mediaId
            publishUrl
            url
        }
    }
`)

const updateUserMutation = gql(`
    mutation updateUserProfile($name: String, $imageUrl: String, $visibility: String) {
        updateUser(name: $name, imageUrl: $imageUrl, visibility: $visibility) {
            id
            name
            visibility
        }
    }
`)

interface UploadForm {
    readonly url: string
    /**
     * Where the uploaded image will live. May be null on the
     * `profileImageUploadForm` path — for that flow we derive the
     * final URL from `url` + the `key` form field, since the gateway
     * doesn't return a separate publishUrl.
     */
    readonly publishUrl: string | null
    readonly fileFieldName: string
    readonly formFields: ReadonlyArray<{ key: string; value: string | null }>
}

function isCompleteForm(
    form: Maybe<{
        url?: string | null
        publishUrl?: string | null
        fileFieldName?: string | null
        formFields?: ReadonlyArray<{ key: string | null; value: string | null } | null> | null
    }>
): form is UploadForm {
    // A "complete" form has the destination URL + the file field name.
    // Form fields are required so the multipart POST gets the bucket /
    // policy / signature parts the S3 endpoint validates. publishUrl is
    // optional — the profile flow returns null and we derive the final
    // image URL from `url + key` instead.
    return !!(form?.url && form.fileFieldName && form.formFields?.length)
}

async function fetchUploadForm(userId: string, contentType: string): Promise<UploadForm> {
    // Try the named profile-image flow first. Note `id` is required —
    // without the user id the gateway returns a null form.
    const profileResult = await gqlFetch(profileImageUploadFormQuery, { id: userId })
    if (isCompleteForm(profileResult.profileImageUploadForm)) {
        return profileResult.profileImageUploadForm
    }
    // Fall back to the generic mediaUploadUrl path (the Android playlist /
    // share-card flow). Note this returns a presigned PUT URL with no
    // form fields — different shape, different code path.
    const mediaResult = await gqlFetch(mediaUploadUrlQuery, { contentType })
    if (isCompleteForm(mediaResult.mediaUploadUrl)) {
        return mediaResult.mediaUploadUrl
    }
    throw new Error(
        'Both profileImageUploadForm and mediaUploadUrl returned incomplete responses — the gateway may not allow this account to upload profile images.'
    )
}

function resolveImageUrl(form: UploadForm): string {
    // The profile form returns a bucket-root URL + a 'key' form field;
    // the resulting object lives at `${url}${key}`.
    if (form.publishUrl) return form.publishUrl
    const keyField = form.formFields.find((f) => f.key === 'key')
    const key = keyField?.value
    if (!key) {
        throw new Error("Upload form has no publishUrl and no 'key' form field")
    }
    // form.url already ends in `/` for the profile flow.
    return form.url.endsWith('/') ? `${form.url}${key}` : `${form.url}/${key}`
}

/**
 * Uploads a profile image. Mirrors DigitalMusicAndroid3P's
 * CustomerProfileService → ProfileService.updateAvatar flow:
 *
 *   1. updateUser(imageUrl: <client URI>, ...) — submits a marker URI
 *      so the server records that an upload is in progress. We use the
 *      published S3 URL after step 3 here, but the Android flow uses
 *      the local content:// URI. Either is accepted; the server only
 *      uses this string for bookkeeping.
 *   2. profileImageUploadForm(id: userId) — gets a presigned form
 *      (Amazon-domain endpoint that proxies to S3 internally).
 *   3. multipart POST the file to form.url with:
 *        - all formFields as form parts
 *        - the file under form.fileFieldName
 *        - x-requested-with: XMLHttpRequest header (CSRF guard)
 *        - credentials: 'include' so MAP cookies travel along
 *      That's it — the Android flow does NOT call publishUploadedMedia
 *      for profile photos. The form upload IS the publish.
 *
 * Returns the resulting profile image URL (form.publishUrl) so the
 * caller can pass it to updateUser({ imageUrl }) afterwards.
 */
export async function uploadProfileImage(file: Blob): Promise<string> {
    logDebug(target, 'uploadProfileImage start', { size: file.size, type: file.type })
    // Read the user id once — the upload form requires it.
    const info = await fetchUserInfo()
    const userId = info.user?.id
    if (!userId) {
        throw new Error('Cannot upload profile image: user id unavailable')
    }

    const contentType = file.type || 'image/jpeg'
    const form = await fetchUploadForm(userId, contentType)

    const body = new FormData()
    for (const field of form.formFields) {
        if (field.key && field.value != null) body.append(field.key, field.value)
    }
    // The file field must come last for S3 pre-signed POSTs.
    body.append(form.fileFieldName, file)

    try {
        // S3 presigned-POST. We send `mode: 'no-cors'` because the
        // silhouette-media-prod-iad bucket doesn't expose CORS headers
        // back to localhost — without no-cors, fetch rejects the
        // response as "Failed to fetch" even though S3 actually accepts
        // the upload. The trade-off is that we can't read the response
        // body or status — we have to assume success if the call doesn't
        // throw. Goes through `nativeFetchRef` (rather than a Vinyl
        // requester) so the response stays opaque on web — Vinyl's
        // requesters reject `!ok` responses, but an opaque response is
        // permanently `ok=false`, so we'd never see a successful upload.
        await nativeFetchRef.value(form.url, {
            method: 'POST',
            body,
            mode: 'no-cors',
        })
    } catch (err) {
        logWarn(target, 'profile image upload POST failed', err)
        throw new Error(
            `Profile image upload network error on ${form.url}: ${(err as Error).message}`
        )
    }

    // No publishUploadedMedia step for profile photos — the multipart
    // POST IS the publish (per Android). Resolve the final URL from
    // either form.publishUrl or the bucket+key in the form fields.
    const url = resolveImageUrl(form)
    logDebug(target, 'uploadProfileImage success', { url })
    return url
}

export interface UpdateUserProfileInput {
    readonly name?: string
    /**
     * URL to write as the user's image. For an upload pass the
     * published S3 URL returned from {@link uploadProfileImage}; for
     * remove-photo pass the gateway's `defaultProfileImage` URL —
     * the server doesn't accept null/empty here, so we explicitly
     * write the default to "clear" the photo.
     */
    readonly imageUrl?: string
    /**
     * Optional renderable representation of the same image that the
     * UI can show while the gateway propagates the write. Typically
     * a data URL (so it survives a page refresh via the persisted
     * overlay). When omitted, `imageUrl` is used for the overlay too
     * — fine for the remove-photo path where imageUrl is already a
     * publicly-fetchable default-avatar URL.
     */
    readonly imagePreviewUrl?: string
    /**
     * Visibility of the user's profile to other users — 'PUBLIC' or
     * 'PRIVATE'. Only sent to the gateway when this field is set.
     */
    readonly visibility?: 'PUBLIC' | 'PRIVATE'
}

export async function updateUserProfile(input: UpdateUserProfileInput): Promise<void> {
    logDebug(target, 'updateUserProfile', {
        nameChanged: input.name !== undefined,
        imageChanged: input.imageUrl !== undefined,
    })
    await gqlFetch(
        updateUserMutation,
        {
            name: input.name ?? null,
            imageUrl: input.imageUrl ?? null,
            visibility: input.visibility ?? null,
        },
        { featureFlags: NO_API_CACHE_FLAGS }
    )
    // Mutation done — drop the userInfo cache so the next read returns fresh
    // data. fetchUserInfoFresh() does the same, but invalidating here covers
    // callers that don't immediately re-read.
    await invalidateGqlCache(userInfoQuery, {})

    // Lay the change down in the local overlay so subsequent reads
    // reflect what we just wrote even when Firefly hasn't propagated
    // the update yet. Profile image writes routinely take 30-40s to
    // surface from the gateway (the bucket is in a different region
    // and the gateway re-derives the user's image list lazily), so
    // image overlays use a 60s TTL. Name updates settle within a few
    // seconds, so the default 15s is plenty for them.
    const IMAGE_OVERLAY_TTL_MS = 60_000
    if (input.name !== undefined) nameOverlay.write(input.name)
    if (input.visibility !== undefined) visibilityOverlay.write(input.visibility)
    if (input.imagePreviewUrl !== undefined) {
        imageUrlOverlay.write(input.imagePreviewUrl, { ttlMs: IMAGE_OVERLAY_TTL_MS })
    } else if (input.imageUrl !== undefined) {
        imageUrlOverlay.write(input.imageUrl, { ttlMs: IMAGE_OVERLAY_TTL_MS })
    }

    // Push the local patch into the observable so every subscriber
    // (header avatar, profile page, etc.) sees the new value without
    // waiting on the next gateway read. Prefer the data-URL preview for
    // the imageUrl patch — that's what survives renders against
    // localhost where the published S3 URL won't load until the CDN
    // catches up.
    const patch: { name?: string; imageUrl?: string; visibility?: 'PUBLIC' | 'PRIVATE' } = {}
    if (input.name !== undefined) patch.name = input.name
    if (input.visibility !== undefined) patch.visibility = input.visibility
    const previewOrUrl = input.imagePreviewUrl ?? input.imageUrl
    if (previewOrUrl !== undefined) patch.imageUrl = previewOrUrl
    patchUserInfoLocally(patch)
}
